import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import cn from 'classnames';

import ConfigContext from '../../../contexts/ConfigContext';

// Components
import Meta from '../../shared/Meta';
import CostEstimator from '../content/CostEstimator';
import FeaturedTrucks from '../FeaturedTrucks';
import CtaImage from '../content/CtaImage';
import LogoList from '../content/LogoList';
import AnyEvent from '../content/AnyEvent';
import FeaturedCuisines from '../FeaturedCuisines';
import CateringCta from '../content/CateringCta';
import HowItWorks from '../content/HowItWorks';
import WhyBookWithUs from '../content/WhyBookWithUs';
import Testimonials from '../content/Testimonials';
import CateringFaq from '../faqs/CateringFaq';

import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';

const featuredTrucks = { featured: true };

function PrivatePartyCatering() {
  const { vehicle_type, city_name, site_title } = useContext(ConfigContext);
  const vehicleType = vehicle_type.toLowerCase();

  return (
    <div className='CateringPage'>
      <Meta
        title={`Food ${vehicle_type} for Private Parties`}
        type='website'
        canonical='/food-truck-catering/private-parties'
        image='https://static.seattlefoodtruck.com/photos/community.webp'
        description={`Book a food ${vehicleType} for your private party in ${city_name}. Birthday parties, anniversaries, graduations, family reunions & more. Get free quotes today.`}
        structuredData={{
          cateringService: {
            name: `Food ${vehicle_type} Private Party Catering in ${city_name}`,
            description: `Book a food ${vehicleType} for your private party in ${city_name}. Birthday parties, anniversaries, graduations, family reunions and more.`,
            url: '/food-truck-catering/private-parties',
            serviceType: 'Food Truck Private Party Catering',
          },
          breadcrumbs: {
            listItems: [
              { '@type': 'ListItem', position: 2, name: 'Catering', item: '/catering' },
              { '@type': 'ListItem', position: 3, name: 'Private Parties', item: '/food-truck-catering/private-parties' }
            ]
          }
        }}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/community.webp' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>Book a Food {vehicle_type} for Your Private Party</h1>
            <p>Skip the stress of cooking for a crowd and let {city_name}&#39;s best food {vehicleType}s handle the catering at your next private party. Whether you&#39;re hosting a <Link to='/food-truck-catering/birthday-parties'>birthday party</Link>, anniversary celebration, graduation, family reunion, backyard BBQ, or pool party, {site_title} connects you with top-rated {vehicleType}s that serve fresh, made-to-order meals your guests will love.</p>
            <p>
              <Link to='/catering/register' className='Button Button--mobile-w100'>Plan Your Private Party</Link>
            </p>
          </div>
        </div>
      </div>

      <section className='Section'>
        <LogoList title='Trusted by leading companies' titleClassName='u-textCenter' className='u-mt1' />
      </section>

      <FeaturedTrucks title={`Popular Food ${vehicle_type}s for Private Parties`} options={featuredTrucks} />

      <WhyBookWithUs title={`Why Book a Food ${vehicle_type} for a Private Party?`} />

      <section className='Section--extraPadding'>
        <h2 className='u-textCenter u-mb3'>The Perfect Food {vehicle_type} for Any Party</h2>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering5.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Birthday Parties & Milestone Celebrations</h3>
            <p>Food {vehicleType}s turn any birthday or milestone into an unforgettable event. From kids&#39; parties with ice cream and sliders to 50th birthday bashes with gourmet tacos and craft desserts, a food {vehicleType} adds excitement and flavor that traditional catering simply can&#39;t match. Guests love the interactive experience and the variety of fresh, made-to-order options.</p>
          </div>
        </div>
      </section>

      <section className='Section'>
        <div className={styles.Cta}>
          <CtaImage image='https://static.seattlefoodtruck.com/photos/catering4.webp' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>Graduations, Reunions & Backyard Gatherings</h3>
            <p>Hosting a graduation party, family reunion, backyard BBQ, or pool party? Food {vehicleType}s are the ultimate crowd-pleaser. They serve a variety of cuisines to satisfy every palate, handle all the cooking and cleanup, and create a fun, casual atmosphere that gets everyone talking. No need to fire up the grill or order stacks of pizza — just let {city_name}&#39;s best {vehicleType}s do the work.</p>
          </div>
        </div>
      </section>

      <HowItWorks />

      <AnyEvent />

      <Testimonials title="See what our customers are saying" />

      <CostEstimator />

      <section className='Section--extraPadding'>
        <FeaturedCuisines />
      </section>

      <section className='Section--extraPadding'>
        <CateringFaq />
      </section>

      <section className='Section--extraPadding'>
        <CateringCta title={`Book a Food ${vehicle_type} for Your Private Party!`} />
      </section>
    </div>
  );
}

export default PrivatePartyCatering;
